import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, ImagePlus, Play, ShieldAlert, X } from "lucide-react";

import AttachmentPreviewModal from "../messaging/AttachmentPreviewModal";
import {
  RETURN_REQUEST_MEDIA_ACCEPT,
  RETURN_REQUEST_MAX_FILE_SIZE_BYTES,
  RETURN_REQUEST_MAX_MEDIA_FILES,
  RETURN_REQUEST_MAX_VIDEO_FILES,
  toReturnRequestAttachmentMeta,
  validateReturnRequestFiles,
} from "../../utils/returnRequestMedia";
import {
  buildReturnRefundPolicyView,
  RETURN_REFUND_POLICY_VERSION,
  RETURN_REFUND_REASON_OPTIONS,
  RETURN_REFUND_REASON_OTHER_CODE,
} from "./returnRefundPolicy";

export const RETURN_REFUND_REASON_MAX_LENGTH = 200;
export const RETURN_REFUND_DETAILS_MAX_LENGTH = 500;

function createReturnRequestMediaPreview(file, index) {
  const mediaType = String(file?.type || "").startsWith("video/") ? "video" : "image";

  return {
    id: `${file?.name || "file"}-${file?.size || 0}-${index}-${Date.now()}`,
    file,
    objectUrl: URL.createObjectURL(file),
    mediaType,
    mimeType: file?.type || null,
    originalName: file?.name || null,
    fileSize: Number(file?.size || 0),
    position: index,
  };
}

function clearReturnRequestMediaPreviews(entries = []) {
  entries.forEach((entry) => {
    if (entry?.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
    }
  });
}

export default function ReturnRefundRequestModal({
  open,
  orderLabel = "",
  orderReference = "",
  submitting = false,
  returnAddress = null,
  onClose,
  onSubmit,
}) {
  const mediaRef = useRef([]);
  const [selectedReasonCode, setSelectedReasonCode] = useState(RETURN_REFUND_REASON_OPTIONS[0].code);
  const [customReason, setCustomReason] = useState("");
  const [details, setDetails] = useState("");
  const [media, setMedia] = useState([]);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const termsPanelId = useId();
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [error, setError] = useState("");
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);

  useEffect(() => {
    mediaRef.current = media;
  }, [media]);

  useEffect(() => () => {
    clearReturnRequestMediaPreviews(mediaRef.current);
  }, []);

  useEffect(() => {
    if (!open) {
      clearReturnRequestMediaPreviews(mediaRef.current);
      mediaRef.current = [];
      setSelectedReasonCode(RETURN_REFUND_REASON_OPTIONS[0].code);
      setCustomReason("");
      setDetails("");
      setMedia([]);
      setPolicyAccepted(false);
      setTermsExpanded(false);
      setError("");
      setActivePreviewIndex(null);
      return;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose?.();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, open, submitting]);

  const activePreview = useMemo(
    () => (activePreviewIndex != null && media[activePreviewIndex]
      ? toReturnRequestAttachmentMeta(
        {
          url: media[activePreviewIndex].objectUrl,
          mediaType: media[activePreviewIndex].mediaType,
          mimeType: media[activePreviewIndex].mimeType,
          originalName: media[activePreviewIndex].originalName,
          fileSize: media[activePreviewIndex].fileSize,
        },
        activePreviewIndex
      )
      : null),
    [activePreviewIndex, media]
  );

  const selectedReasonOption = useMemo(
    () => RETURN_REFUND_REASON_OPTIONS.find((option) => option.code === selectedReasonCode) || RETURN_REFUND_REASON_OPTIONS[0],
    [selectedReasonCode]
  );

  if (!open) return null;

  const resolvedReason = (
    selectedReasonOption?.allowCustomReason ? customReason : selectedReasonOption?.label || ""
  )
    .replace(/\s+/g, " ")
    .trim();

  const normalizedDetails = details.replace(/\s+/g, " ").trim();
  const policyView = buildReturnRefundPolicyView({
    policy: {
      reasonCode: selectedReasonOption?.code || RETURN_REFUND_REASON_OTHER_CODE,
      shippingResponsibility: selectedReasonOption?.shippingResponsibility || null,
      issueClassification: selectedReasonOption?.issueClassification || null,
      version: RETURN_REFUND_POLICY_VERSION,
    },
    reason: resolvedReason || selectedReasonOption?.label || "",
    status: "REQUESTED",
    orderReference,
    returnAddress,
  });
  const returnAddressInfo = policyView?.returnAddress || null;

  const getPolicyBadgeClasses = (tone) => {
    switch (tone) {
      case "sky":
        return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-200";
      case "amber":
        return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200";
      case "rose":
        return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200";
      case "orange":
      default:
        return "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200";
    }
  };

  const handleMediaChange = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (nextFiles.length === 0) return;

    const combinedFiles = [...media.map((entry) => entry.file), ...nextFiles];
    const validation = validateReturnRequestFiles(combinedFiles);
    if (!validation.ok) {
      setError(validation.error);
      return;
    }

    setError("");
    setMedia((current) => [
      ...current,
      ...nextFiles.map((file, index) => createReturnRequestMediaPreview(file, current.length + index)),
    ]);
  };

  const handleRemoveMedia = (mediaId) => {
    setMedia((current) => {
      const target = current.find((entry) => entry.id === mediaId);
      if (target?.objectUrl) {
        URL.revokeObjectURL(target.objectUrl);
      }
      return current.filter((entry) => entry.id !== mediaId);
    });
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!resolvedReason) {
      setError(selectedReasonOption?.allowCustomReason ? "Please tell us what happened before submitting this request." : "Please select a return/refund reason.");
      return;
    }

    if (resolvedReason.length > RETURN_REFUND_REASON_MAX_LENGTH) {
      setError(`Reason must be ${RETURN_REFUND_REASON_MAX_LENGTH} characters or less.`);
      return;
    }

    if (normalizedDetails.length > RETURN_REFUND_DETAILS_MAX_LENGTH) {
      setError(`Details must be ${RETURN_REFUND_DETAILS_MAX_LENGTH} characters or less.`);
      return;
    }

    const validation = validateReturnRequestFiles(media.map((entry) => entry.file));
    if (!validation.ok) {
      setError(validation.error);
      return;
    }

    if (!policyAccepted) {
      setError("Please acknowledge the return and refund terms before submitting your request.");
      return;
    }

    try {
      await onSubmit?.({
        reason: resolvedReason,
        reasonCode: selectedReasonOption?.code || RETURN_REFUND_REASON_OTHER_CODE,
        details: normalizedDetails || null,
        policyAccepted,
        media: media.map((entry) => entry.file),
      });
    } catch (submitError) {
      setError(submitError?.message || "Unable to submit this return/refund request right now.");
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-[1px]" onClick={() => !submitting && onClose?.()} />
      <div className="pointer-events-none fixed inset-0 z-[60] flex items-end justify-center px-0 pb-[env(safe-area-inset-bottom)] pt-3 sm:items-center sm:px-5 sm:py-6 lg:px-6 lg:py-10">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="return-refund-modal-title"
          aria-describedby="return-refund-modal-description"
          className="pointer-events-auto flex max-h-[calc(100dvh-0.75rem)] w-full max-w-2xl flex-col overflow-hidden rounded-t-[2rem] border border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-900 sm:max-h-[calc(100dvh-3rem)] sm:rounded-[2rem] lg:max-h-[calc(100dvh-5rem)]"
        >
          <div className="flex items-start justify-between gap-4 border-b border-neutral-100 px-4 py-4 dark:border-neutral-800 sm:px-5">
            <div className="min-w-0">
              <h2
                id="return-refund-modal-title"
                className="text-base font-semibold text-neutral-900 dark:text-neutral-100"
              >
                Request Return / Refund
              </h2>
              <p
                id="return-refund-modal-description"
                className="mt-1 text-sm leading-relaxed text-neutral-500 dark:text-neutral-400"
              >
                Review the return terms, choose the closest issue, and add photos or videos so the seller can review your request faster.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {orderLabel ? (
                  <div className="inline-flex max-w-full items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-[11px] font-semibold text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200">
                    <span className="truncate">{orderLabel}</span>
                  </div>
                ) : null}
                {orderReference ? (
                  <div className="inline-flex max-w-full items-center rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
                    <span className="truncate">Order ID: {orderReference}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-500 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
              aria-label="Close return/refund request modal"
            >
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5">
              <section className="rounded-2xl border border-neutral-200 bg-neutral-50/80 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/40">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                    <ShieldAlert size={15} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      What issue should the seller review?
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Choose the closest reason below. You can add more context in the details section.
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {RETURN_REFUND_REASON_OPTIONS.map((reason) => {
                    const isSelected = selectedReasonCode === reason.code;

                    return (
                      <button
                        key={reason.code}
                        type="button"
                        onClick={() => {
                          setSelectedReasonCode(reason.code);
                          setError("");
                        }}
                        className={[
                          "flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm transition",
                          isSelected
                            ? "border-neutral-900 bg-neutral-900 text-white shadow-sm dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900"
                            : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-700 dark:hover:bg-neutral-800",
                        ].join(" ")}
                        aria-pressed={isSelected}
                      >
                        <span className="pr-3 leading-snug">{reason.label}</span>
                        <span
                          className={[
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold",
                            isSelected
                              ? "border-current"
                              : "border-neutral-300 text-transparent dark:border-neutral-700",
                          ].join(" ")}
                        >
                          •
                        </span>
                      </button>
                    );
                  })}
                </div>

                {selectedReasonOption?.allowCustomReason ? (
                  <div className="mt-4">
                    <label
                      htmlFor="return-refund-custom-reason"
                      className="block text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400"
                    >
                      Tell us what happened
                    </label>
                    <textarea
                      id="return-refund-custom-reason"
                      value={customReason}
                      onChange={(event) => {
                        setCustomReason(event.target.value.slice(0, RETURN_REFUND_REASON_MAX_LENGTH));
                        setError("");
                      }}
                      rows={3}
                      maxLength={RETURN_REFUND_REASON_MAX_LENGTH}
                      placeholder="Describe the issue so the seller can review it properly."
                      className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-500/20"
                    />
                    <div className="mt-1 text-right text-[11px] text-neutral-400 dark:text-neutral-500">
                      {customReason.trim().length}/{RETURN_REFUND_REASON_MAX_LENGTH}
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="rounded-2xl border border-orange-200/80 bg-gradient-to-br from-orange-50 via-white to-orange-50/70 p-3.5 dark:border-orange-900/40 dark:from-orange-950/30 dark:via-neutral-900 dark:to-neutral-900">
                <div className="flex items-start gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-200">
                    <ShieldAlert size={15} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      Return & refund terms
                    </p>
                    <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                      These terms apply to this request before the seller decides whether a return shipment or refund is allowed.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setTermsExpanded((current) => !current)}
                  aria-expanded={termsExpanded}
                  aria-controls={termsPanelId}
                  className="mt-4 w-full rounded-2xl border border-orange-200/70 bg-white/90 px-3.5 py-3 text-left transition hover:border-orange-300 hover:bg-white dark:border-orange-900/40 dark:bg-neutral-950/40 dark:hover:border-orange-800"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        Review return terms, shipping, and return address
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                        {returnAddressInfo?.isAvailable
                          ? "The business return address on file is included below."
                          : "If the business return address is still missing, the shopper must confirm it through chat before shipping anything."}
                      </p>
                    </div>

                    <div className="inline-flex items-center gap-2 self-start rounded-full border border-orange-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200">
                      {termsExpanded ? "Hide details" : "View details"}
                      <ChevronDown
                        size={14}
                        className={termsExpanded ? "rotate-180 transition-transform duration-200" : "rotate-0 transition-transform duration-200"}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {policyView.badges.map((badge) => (
                      <span
                        key={`${badge.label}-${badge.tone}`}
                        className={[
                          "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold",
                          getPolicyBadgeClasses(badge.tone),
                        ].join(" ")}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </div>
                </button>

                <div
                  id={termsPanelId}
                  aria-hidden={!termsExpanded}
                  className={termsExpanded ? "mt-4 max-h-[240rem] overflow-hidden opacity-100 transition-all duration-300" : "max-h-0 overflow-hidden opacity-0 transition-all duration-300"}
                >
                  <div className="space-y-4">
                    {returnAddressInfo ? (
                      <div className="rounded-2xl border border-orange-200/70 bg-white/90 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                          {returnAddressInfo.title}
                        </div>
                        <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                          {returnAddressInfo.body}
                        </div>

                        {Array.isArray(returnAddressInfo.lines) && returnAddressInfo.lines.length > 0 ? (
                          <div className="mt-3 rounded-2xl border border-orange-200/60 bg-orange-50/70 px-3 py-2.5 dark:border-orange-900/40 dark:bg-orange-950/20">
                            {returnAddressInfo.lines.map((line) => (
                              <div key={line} className="break-words text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                {line}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {policyView.highlights.map((highlight) => (
                        <div
                          key={highlight.title}
                          className="rounded-2xl border border-orange-200/70 bg-white/90 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40"
                        >
                          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                            {highlight.title}
                          </div>
                          <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                            {highlight.body}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-2xl border border-orange-200/70 bg-white/90 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                        Manual courier return steps
                      </div>
                      <div className="mt-2 space-y-2">
                        {policyView.courierSteps.map((step) => (
                          <div key={step} className="flex items-start gap-2.5 text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">
                            <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500 dark:bg-orange-300" />
                            <span className="break-words">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <label className="mt-4 flex items-start gap-3 rounded-2xl border border-orange-200/70 bg-white/90 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                  <input
                    type="checkbox"
                    checked={policyAccepted}
                    onChange={(event) => {
                      setPolicyAccepted(event.target.checked);
                      setError("");
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-orange-500 focus:ring-2 focus:ring-orange-500/30 dark:border-neutral-700 dark:bg-neutral-900"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {policyView.acknowledgementText}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                      This acknowledgment is required before you can submit the request.
                    </span>
                  </span>
                </label>
              </section>

              <section className="space-y-2">
                <label
                  htmlFor="return-refund-details"
                  className="block text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400"
                >
                  Details
                </label>
                <textarea
                  id="return-refund-details"
                  value={details}
                  onChange={(event) => {
                    setDetails(event.target.value.slice(0, RETURN_REFUND_DETAILS_MAX_LENGTH));
                    setError("");
                  }}
                  rows={5}
                  maxLength={RETURN_REFUND_DETAILS_MAX_LENGTH}
                  placeholder="Add helpful details about the item condition, issue, or what you received."
                  className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-500/20"
                />
                <div className="text-right text-[11px] text-neutral-400 dark:text-neutral-500">
                  {details.trim().length}/{RETURN_REFUND_DETAILS_MAX_LENGTH}
                </div>
              </section>

              <section className="rounded-2xl border border-dashed border-neutral-300 bg-gradient-to-br from-white to-neutral-50 p-3.5 dark:border-neutral-700 dark:from-neutral-900/70 dark:to-neutral-900/40">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        Add photos or videos
                      </p>
                      <span className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                        {media.length}/{RETURN_REQUEST_MAX_MEDIA_FILES} selected
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Up to {RETURN_REQUEST_MAX_MEDIA_FILES} files total, with up to {RETURN_REQUEST_MAX_VIDEO_FILES} videos and a maximum of {Math.round(RETURN_REQUEST_MAX_FILE_SIZE_BYTES / (1024 * 1024))} MB each.
                    </p>
                  </div>

                  <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-3.5 py-2.5 text-xs font-semibold text-neutral-700 shadow-sm transition hover:border-neutral-400 hover:bg-neutral-50 focus-within:ring-2 focus-within:ring-indigo-500/20 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-600 dark:hover:bg-neutral-800 sm:w-auto">
                    <ImagePlus size={14} />
                    Add Evidence
                    <input
                      type="file"
                      accept={RETURN_REQUEST_MEDIA_ACCEPT}
                      multiple
                      className="hidden"
                      onChange={handleMediaChange}
                    />
                  </label>
                </div>

                {media.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {media.map((entry, index) => (
                      <div
                        key={entry.id}
                        className="group relative w-[96px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-900"
                      >
                        <button
                          type="button"
                          onClick={() => setActivePreviewIndex(index)}
                          className="relative block h-24 w-full overflow-hidden bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset dark:bg-neutral-800"
                        >
                          {entry.mediaType === "video" ? (
                            <>
                              <video
                                src={entry.objectUrl}
                                className="h-full w-full object-cover"
                                muted
                                playsInline
                                preload="metadata"
                              />
                              <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                                Video
                              </span>
                              <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/55 shadow-sm">
                                  <Play size={14} fill="currentColor" />
                                </span>
                              </span>
                            </>
                          ) : (
                            <img
                              src={entry.objectUrl}
                              alt={entry.originalName || "Evidence preview"}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleRemoveMedia(entry.id)}
                          className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                          aria-label={`Remove ${entry.originalName || "evidence file"}`}
                        >
                          <X size={12} />
                        </button>

                        <div className="border-t border-neutral-100 px-2.5 py-1.5 text-[10px] font-medium text-neutral-500 dark:border-neutral-800 dark:text-neutral-300">
                          <span className="block truncate">
                            {entry.originalName || (entry.mediaType === "video" ? "Evidence video" : "Evidence image")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-neutral-200/80 bg-white/80 px-3.5 py-3 text-sm text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950/40 dark:text-neutral-400">
                    Add clear photos or short videos if you want to show damage, the wrong item, or missing accessories before you submit.
                  </div>
                )}
              </section>

              {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
                  {error}
                </div>
              ) : null}
            </div>

            <div className="shrink-0 border-t border-neutral-100 bg-white/95 px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] dark:border-neutral-800 dark:bg-neutral-900/95 supports-[backdrop-filter]:backdrop-blur sm:px-5 sm:pb-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  disabled={submitting}
                  className="rounded-2xl border border-neutral-300 px-4 py-3 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
                >
                  Keep Order
                </button>
                <button
                  type="submit"
                  disabled={submitting || !policyAccepted}
                  className="rounded-2xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-orange-500 dark:hover:bg-orange-400"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {activePreview ? (
        <AttachmentPreviewModal
          attachment={activePreview}
          onClose={() => setActivePreviewIndex(null)}
        />
      ) : null}
    </>
  );
}
